<script setup>
import { computed, ref } from 'vue';
import { withShareUtm } from 'dashboard/helper/lais/listingCardPresenter';

const props = defineProps({
  url: { type: String, default: '' },
  title: { type: String, default: 'Imovel' },
  text: { type: String, default: '' },
  source: { type: String, default: 'dashboard' },
  businessContext: { type: String, default: 'rental' },
  variant: {
    type: String,
    default: 'icon',
    validator: value => ['icon', 'text'].includes(value),
  },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['shared', 'error']);

const isSharing = ref(false);
const shareUrl = computed(() =>
  withShareUtm(props.url, {
    medium: 'copy_share',
    businessContext: props.businessContext || props.source,
  })
);
const label = computed(() =>
  props.variant === 'text' ? 'Compartilhar' : 'Compartilhar imovel'
);

const buildShareUrl = medium =>
  withShareUtm(props.url, {
    medium,
    businessContext: props.businessContext || props.source,
  });

const copyToClipboard = async value => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return 'clipboard';
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', 'true');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
  return 'clipboard_legacy';
};

const share = async () => {
  if (props.disabled || !props.url) return;

  isSharing.value = true;
  try {
    if (navigator.share) {
      const url = buildShareUrl('custom_share');
      const payload = {
        title: props.title,
        text: props.text || props.title,
        url,
      };
      await navigator.share(payload);
      emit('shared', {
        method: 'navigator_share',
        medium: 'custom_share',
        url,
      });
      return;
    }

    const url = buildShareUrl('copy_share');
    const method = await copyToClipboard(url);
    emit('shared', { method, medium: 'copy_share', url });
  } catch (error) {
    if (error?.name === 'AbortError') return;
    emit('error', error);
  } finally {
    isSharing.value = false;
  }
};
</script>

<template>
  <button
    type="button"
    :disabled="disabled || isSharing || !shareUrl"
    class="inline-flex items-center justify-center gap-2 rounded-lais border border-lais-border bg-white font-semibold text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
    :class="variant === 'text' ? 'h-9 px-3 text-[13px]' : 'size-9'"
    :aria-label="label"
    @click.stop="share"
  >
    <span
      class="size-4"
      :class="
        isSharing ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-share-2'
      "
      aria-hidden="true"
    />
    <span v-if="variant === 'text'">{{ label }}</span>
  </button>
</template>
